import { useWindowSize } from "hooks/useWindowSize";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Banner } from "components/DesignSystem/Banner/Banner";
import Card from "components/DesignSystem/Card/Card";
import Loader from "components/DesignSystem/Loader/loader";
import { Switch } from "components/DesignSystem/Switch/Switch";
import DoubleGreenTick from "static/images/DoubleGreenTick.svg";
import { RND_SECTION_TYPE } from "constants/financialClosing";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import {
  RnDBifurcationFieldName,
  RnDBifurcationFieldValue,
  RnDBifurcationSection as RnDBifurcationSectionType,
  useGetRnDBifurcationQuery,
  useUpdateRnDBifurcationFieldMutation,
  useUpdateRnDBifurcationVisibilityMutation,
} from "store/apis/financialClosing";
import { BackendError } from "types/utils/error";
import { CheckListAccordion } from "./CheckListAccordion";
import { RnDBifurcation } from "./RnDBifurcation";

export type RnDSaveState = {
  isSaving: boolean;
  isSaved: boolean;
};

type RnDSectionType = (typeof RND_SECTION_TYPE)[keyof typeof RND_SECTION_TYPE];

export type RnDUpdateField = (
  sectionType: RnDSectionType,
  vendorId: string,
  field: RnDBifurcationFieldName,
  value: RnDBifurcationFieldValue
) => void;

const EMPTY_SECTION: RnDBifurcationSectionType = {
  vendors: [],
  previous_month_reference: [],
  previous_month_date: null,
};

export const SaveIndicator = ({ isSaving, isSaved }: RnDSaveState) => (
  <Async.Root
    isLoading={isSaving}
    customLoader={
      <div className="t-flex t-items-center">
        <Loader customType="secondary" />
      </div>
    }
    isEmpty={false}
    isSuccess={isSaved}
  >
    <Async.Empty>{null}</Async.Empty>
    <Async.Success>
      <span className="t-flex t-items-center t-gap-1 t-text-text-30 t-text-body-sm">
        <img src={DoubleGreenTick} alt="Saved" />
        Changes saved
      </span>
    </Async.Success>
  </Async.Root>
);

export const useRnDUpdateField = (
  entityId: string,
  financialClosingId: string
): { updateField: RnDUpdateField; saveState: RnDSaveState } => {
  const { alertToast } = useToast();

  const [updateField, { isLoading: isSaving, isSuccess: isSaved }] =
    useUpdateRnDBifurcationFieldMutation();

  const handleUpdateField: RnDUpdateField = (
    sectionType,
    vendorId,
    field,
    value
  ) => {
    void (async () => {
      try {
        await updateField({
          entityId,
          financialClosingId,
          payload: {
            section_type: sectionType,
            vendor_id: vendorId,
            field,
            value,
          },
        }).unwrap();
      } catch (error) {
        alertToast(
          {
            message:
              (error as BackendError).data?.error?.message ??
              "Something went wrong",
          },
          error as Error
        );
      }
    })();
  };

  return { updateField: handleUpdateField, saveState: { isSaving, isSaved } };
};

const RnDBifurcationInnerSections = ({
  isFormEditable,
  intercompanyCompensation,
  consultantContractors,
  updateField,
}: {
  isFormEditable: boolean;
  intercompanyCompensation: RnDBifurcationSectionType;
  consultantContractors: RnDBifurcationSectionType;
  updateField: RnDUpdateField;
}) => {
  return (
    <div className="t-flex t-flex-col t-gap-4 t-min-w-0">
      <Banner
        type="neutral"
        description="Classify each vendor's expense as US or Non-US R&D. Non-US R&D is amortized over 15 years under IRC §174 — affecting your taxable income and tax liability."
      />
      <CheckListAccordion.Root
        type="multiple"
        defaultValue={["intercompany", "consultants"]}
      >
        <CheckListAccordion.Item value="intercompany">
          <CheckListAccordion.Trigger iconLeft>
            <div className="t-flex t-items-center t-gap-3">
              <span>Intercompany Compensation</span>
            </div>
          </CheckListAccordion.Trigger>
          <CheckListAccordion.Content>
            <div className="t-pt-2">
              <RnDBifurcation
                vendors={intercompanyCompensation.vendors}
                previousMonthReference={
                  intercompanyCompensation.previous_month_reference
                }
                previousMonthDate={intercompanyCompensation.previous_month_date}
                sectionType={RND_SECTION_TYPE.INTERCOMPANY_COMPENSATION}
                isFormEditable={isFormEditable}
                updateField={(vendorId, field, value) =>
                  updateField(
                    RND_SECTION_TYPE.INTERCOMPANY_COMPENSATION,
                    vendorId,
                    field,
                    value
                  )
                }
              />
            </div>
          </CheckListAccordion.Content>
        </CheckListAccordion.Item>

        <hr className="t-border-t t-border-neutral-10 t-mb-6 t-mt-0" />

        <CheckListAccordion.Item value="consultants">
          <CheckListAccordion.Trigger iconLeft>
            <div className="t-flex t-items-center t-gap-2">
              <span>Consultants & Contractors</span>
            </div>
          </CheckListAccordion.Trigger>
          <CheckListAccordion.Content>
            <div className="t-pt-2">
              <RnDBifurcation
                vendors={consultantContractors.vendors}
                previousMonthReference={
                  consultantContractors.previous_month_reference
                }
                previousMonthDate={consultantContractors.previous_month_date}
                sectionType={RND_SECTION_TYPE.CONSULTANT_CONTRACTOR}
                isFormEditable={isFormEditable}
                updateField={(vendorId, field, value) =>
                  updateField(
                    RND_SECTION_TYPE.CONSULTANT_CONTRACTOR,
                    vendorId,
                    field,
                    value
                  )
                }
              />
            </div>
          </CheckListAccordion.Content>
        </CheckListAccordion.Item>
      </CheckListAccordion.Root>
    </div>
  );
};

export const RnDBifurcationSection = ({
  entityId,
  financialClosingId,
  isFormEditable,
  showOuterAccordion = true,
  updateField: updateFieldProp,
}: {
  entityId: string;
  financialClosingId: string;
  isFormEditable: boolean;
  showOuterAccordion?: boolean;
  updateField?: RnDUpdateField;
}) => {
  const { isAnyServiceUser } = useRoleBasedView();
  const { isMobile } = useWindowSize();
  const { alertToast } = useToast();
  const { data, isLoading, isError, isSuccess } = useGetRnDBifurcationQuery(
    { entityId, financialClosingId },
    { skip: !entityId || !financialClosingId }
  );
  const { updateField: ownUpdateField } = useRnDUpdateField(
    entityId,
    financialClosingId
  );
  const updateField = updateFieldProp ?? ownUpdateField;

  const [updateVisibility, { isLoading: isUpdatingVisibility }] =
    useUpdateRnDBifurcationVisibilityMutation();

  const shareWithClient = data?.share_with_client ?? false;

  const handleShareWithClientChange = async (checked: boolean) => {
    try {
      await updateVisibility({
        entityId,
        financialClosingId,
        payload: { field: "share_with_client", value: checked },
      }).unwrap();
    } catch (error) {
      alertToast(
        {
          message:
            (error as BackendError).data?.error?.message ??
            "Something went wrong",
        },
        error as Error
      );
    }
  };

  const intercompanyCompensation =
    data?.intercompany_compensation ?? EMPTY_SECTION;
  const consultantContractors = data?.consultant_contractors ?? EMPTY_SECTION;

  if (!isAnyServiceUser && !shareWithClient) {
    return null;
  }

  const innerSections = (
    <Async.Root
      isLoading={isLoading}
      isError={isError}
      isSuccess={isSuccess}
      isEmpty={false}
    >
      <Async.ErrorHandler>
        <p className="t-text-body-sm t-text-red t-py-4">
          Failed to load R&D data
        </p>
      </Async.ErrorHandler>
      <Async.Success>
        <RnDBifurcationInnerSections
          isFormEditable={isFormEditable}
          intercompanyCompensation={intercompanyCompensation}
          consultantContractors={consultantContractors}
          updateField={updateField}
        />
      </Async.Success>
    </Async.Root>
  );

  if (!showOuterAccordion) {
    return innerSections;
  }

  return (
    <Card.Root variant="primary" className="t-rounded-lg t-min-w-0 sm:!t-px-3">
      <Card.Body className="t-min-w-0">
        <CheckListAccordion.Root type="single" collapsible>
          <CheckListAccordion.Item value="rnd" className="t-group">
            <CheckListAccordion.Trigger>
              <span className="t-flex t-flex-col">
                R&D Expense Bifurcation
                {isAnyServiceUser && (
                  <span className="t-hidden sm:t-block t-text-body-sm t-font-normal t-text-text-30">
                    Share with client
                  </span>
                )}
              </span>
              {isAnyServiceUser && (
                <span
                  className="t-flex t-items-center t-gap-2 t-ml-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="t-text-body-sm t-text-text-30 sm:t-hidden">
                    Share with client
                  </span>
                  <Switch
                    size={isMobile ? "small" : "regular"}
                    checked={shareWithClient}
                    disabled={isUpdatingVisibility}
                    onCheckedChange={(checked) =>
                      void handleShareWithClientChange(checked)
                    }
                  />
                </span>
              )}
            </CheckListAccordion.Trigger>
            <CheckListAccordion.Content>
              <div className="t-flex t-flex-col t-gap-2 t-pt-2 t-min-w-0">
                {innerSections}
              </div>
            </CheckListAccordion.Content>
          </CheckListAccordion.Item>
        </CheckListAccordion.Root>
      </Card.Body>
    </Card.Root>
  );
};
